import React from 'react';
import Dialog from '../../components/Dialog';
import { useTypeToConfirm } from '../../components/TypeToConfirm';
import { IconLock } from '../../components/dialogIcons';

/*
  Closing a count cycle.

  Built 5 September 2026 to Group E's approved drawing `dialog-close-cycle-*.png`.

  **It was the grey box the browser draws**, which OnHand cannot style and which
  said one sentence: "Close cycle Q1 2027 Count? Reps will no longer be able to
  submit." Closing a cycle ends the quarter's counting for every location at
  once, and **the two numbers that decide whether it is the right moment were
  nowhere**: how many sheets are not finished, and how many are waiting on the
  administrator's own approval.

  **The owner confirmed the name on 3 September 2026.** He called it "Close
  count" in passing, and it says "Close cycle": one ends the whole quarter across
  every location, and nothing in OnHand closes a single count. Put to him, and he
  said the button is right and he had it wrong. So the dialog is drawn to make
  the difference unmistakable.
*/
export function cycleCloseFigures(progress = []) {
  const notFinished = progress.filter(p => p.status === 'not_started' || p.status === 'in_progress').length;
  const waiting     = progress.filter(p => p.status === 'submitted').length;
  return { notFinished, waiting };
}

export default function CloseCycleDialog({ cycle, progress = [], onConfirm, onClose, busy }) {
  const { notFinished, waiting } = cycleCloseFigures(progress);
  // Type the site's name first (question 24, 21 September 2026). Two lines:
  // this one and the field below, so taking it off again is easy.
  const confirm = useTypeToConfirm();
  return (
    <Dialog
      kind="danger" icon={<IconLock />}
      title={'Close ' + (cycle?.name || 'this cycle') + '?'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={onClose}>Keep it open</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={busy || !confirm.ready}>
            {busy ? 'Closing...' : 'Close the cycle'}
          </button>
        </>
      }
    >
      <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6 }}>
        Reps will not be able to submit anything more, and no sheet in it can be reopened.
      </p>

      {/* The two numbers that decide whether this is the right moment, which the
          browser's own box could not have shown. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <span>
          <span className="form-lbl" style={{ margin: 0, display: 'block' }}>Not finished</span>
          <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: notFinished ? 'var(--critical-ink)' : 'var(--text-primary)' }}>
            {notFinished} sheet{notFinished === 1 ? '' : 's'}
          </span>
        </span>
        <span>
          <span className="form-lbl" style={{ margin: 0, display: 'block' }}>Waiting for your approval</span>
          <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>
            {waiting} sheet{waiting === 1 ? '' : 's'}
          </span>
        </span>
      </div>

      <div style={{ marginTop: 16, borderRadius: 12, padding: '13px 15px', fontSize: 13, lineHeight: 1.55, background: 'var(--critical-bg)', color: 'var(--critical-ink)' }}>
        This cannot be undone. The next cycle is a fresh set of sheets.
      </div>

      {confirm.field}
    </Dialog>
  );
}
